import { useState, useMemo, useEffect, useCallback } from 'react';

/**
 * Headless search + filter + sort engine for list screens.
 *
 * Owns the search text (debounced), the per-filter values, and the sort
 * key/direction, and returns a fully processed array (searched -> filtered ->
 * sorted) plus the handlers/counts needed to drive the shared presentational
 * controls in ./ListControls (SearchInput, FilterSelect, SortControl,
 * ResultSummary). It renders nothing, so feature files stay in charge of how the
 * processed rows are displayed -- including bespoke groupings (see classroom.js).
 *
 * Config:
 *   searchFields   string[]  -> item[field] values joined into the search text
 *   searchAccessor (item, ctx) => string  -> full control; wins over searchFields
 *                    Multi-word queries are token-ANDed: every whitespace-separated
 *                    token must appear somewhere in the item's search text.
 *   filters        [{ key, label, initial?, allOption?, options, predicate }]
 *                    options: [{value,label}] OR (items, ctx) => [{value,label}]
 *                    predicate: (item, value, ctx) => bool  (skipped for the "all" value)
 *                    initial: seeds the filter to a non-"all" value on mount; the
 *                    predicate DOES apply to it, and clear() returns to it.
 *   sortOptions    [{ key, label, accessor, type? }]  (type:'number' for numeric compare)
 *   initialSort    { key, direction }  -> default active sort (else unsorted)
 *   sortable       boolean (default true) -> false hides the sort control
 *   debounceMs     number (default 250)
 *   context        arbitrary object passed to accessors/predicates (a memo dep,
 *                    so keep it stable -- wrap lookup maps in one object)
 *
 * Note: `filters`, `sortOptions`, `searchAccessor`, and `context` are memo
 * dependencies. Define them as stable references (module-level functions or a
 * single `context` object) to avoid needless re-processing.
 */

const DEFAULT_ALL = 'all';

const readValue = (eventOrValue) =>
  eventOrValue && typeof eventOrValue === 'object' && 'target' in eventOrValue
    ? eventOrValue.target.value
    : eventOrValue;

// The "all" sentinel (predicate skipped) and the seeded default are distinct:
// a filter may default to a real value (`initial`) whose predicate still runs.
const filterAllValue = (filter) => filter.allOption?.value ?? DEFAULT_ALL;
const filterDefaultValue = (filter) => filter.initial ?? filterAllValue(filter);

const useListControls = (items, config = {}) => {
  const {
    searchFields,
    searchAccessor,
    filters = [],
    sortOptions = [],
    initialSort,
    sortable = true,
    debounceMs = 250,
    context,
  } = config;

  // Search: `searchInput` is bound to the input (immediate); `searchQuery` is
  // the debounced value that actually drives filtering.
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setSearchQuery(searchInput), debounceMs);
    return () => clearTimeout(handle);
  }, [searchInput, debounceMs]);

  const onSearchChange = useCallback((eventOrValue) => {
    setSearchInput(readValue(eventOrValue) ?? '');
  }, []);

  // Filter values keyed by filter.key, seeded from each filter's default.
  const [filterValues, setFilterValues] = useState(() => {
    const seed = {};
    filters.forEach((f) => {
      seed[f.key] = filterDefaultValue(f);
    });
    return seed;
  });

  const onFilterChange = useCallback((key, eventOrValue) => {
    const value = readValue(eventOrValue);
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Sort: unsorted (key null) unless an initialSort is supplied.
  const [sortState, setSortState] = useState(
    () => initialSort ?? { key: null, direction: 'asc' }
  );

  const processed = useMemo(() => {
    let result = Array.isArray(items) ? items.slice() : [];

    const query = searchQuery.trim().toLowerCase();
    if (query) {
      // Token-AND: "lincoln smith" matches items whose search text contains
      // both words anywhere (e.g. school "Lincoln" AND teacher "Smith").
      const tokens = query.split(/\s+/);
      result = result.filter((item) => {
        let text;
        if (searchAccessor) {
          text = searchAccessor(item, context);
        } else if (searchFields) {
          text = searchFields.map((field) => item?.[field] ?? '').join(' ');
        } else {
          text = '';
        }
        const haystack = String(text ?? '').toLowerCase();
        return tokens.every((token) => haystack.includes(token));
      });
    }

    filters.forEach((f) => {
      const value = filterValues[f.key];
      if (value === undefined || value === filterAllValue(f)) return;
      if (typeof f.predicate === 'function') {
        result = result.filter((item) => f.predicate(item, value, context));
      }
    });

    if (sortState.key) {
      const option = sortOptions.find((o) => o.key === sortState.key);
      if (option && typeof option.accessor === 'function') {
        // Decorate-sort-undecorate: the accessor runs once per item instead of
        // twice per comparison, which matters when accessors do lookups.
        const dir = sortState.direction === 'desc' ? -1 : 1;
        const decorated = result.map((item) => ({
          item,
          sortKey: option.accessor(item, context),
        }));
        decorated.sort((a, b) => {
          if (option.type === 'number') {
            return ((Number(a.sortKey) || 0) - (Number(b.sortKey) || 0)) * dir;
          }
          return String(a.sortKey ?? '').localeCompare(String(b.sortKey ?? '')) * dir;
        });
        result = decorated.map((d) => d.item);
      }
    }

    return result;
  }, [
    items,
    searchQuery,
    filters,
    filterValues,
    sortOptions,
    sortState,
    searchAccessor,
    searchFields,
    context,
  ]);

  const sortIsDefault = initialSort
    ? sortState.key === initialSort.key &&
      sortState.direction === initialSort.direction
    : sortState.key === null;

  const isFiltered = useMemo(() => {
    if (searchQuery.trim() !== '') return true;
    if (filters.some((f) => filterValues[f.key] !== filterDefaultValue(f))) return true;
    return !sortIsDefault;
  }, [searchQuery, filters, filterValues, sortIsDefault]);

  // Changes whenever the effective search/filter state changes — pagination
  // page-reset effects should key off this, not off resultCount (two different
  // filters can yield equal counts).
  const filterSignature = JSON.stringify([searchQuery.trim().toLowerCase(), filterValues]);

  const filterList = filters.map((f) => ({
    // Named filterKey, not key: these entries are spread into <FilterSelect>,
    // and React swallows a spread prop literally named `key`.
    filterKey: f.key,
    label: f.label,
    value: filterValues[f.key] ?? filterDefaultValue(f),
    onChange: (eventOrValue) => onFilterChange(f.key, eventOrValue),
    options:
      typeof f.options === 'function'
        ? f.options(items, context)
        : f.options || [],
    allOption: f.allOption ?? { value: DEFAULT_ALL, label: 'All' },
  }));

  const filterMap = {};
  filterList.forEach((f) => {
    filterMap[f.filterKey] = f;
  });

  const sort = {
    // Named `activeKey`, not `key`: this object is spread as {...sort} onto
    // <SortControl>, and React would swallow a prop literally named `key`.
    activeKey: sortState.key,
    direction: sortState.direction,
    options: sortable ? sortOptions : [],
    onChangeKey: (eventOrValue) => {
      const key = readValue(eventOrValue);
      setSortState((prev) => ({ ...prev, key: key || null }));
    },
    onToggleDirection: () =>
      setSortState((prev) => ({
        ...prev,
        direction: prev.direction === 'desc' ? 'asc' : 'desc',
      })),
    setSort: (next) => setSortState(next),
  };

  const clear = useCallback(() => {
    setSearchInput('');
    setSearchQuery('');
    setFilterValues(() => {
      const reset = {};
      filters.forEach((f) => {
        reset[f.key] = filterDefaultValue(f);
      });
      return reset;
    });
    setSortState(initialSort ?? { key: null, direction: 'asc' });
  }, [filters, initialSort]);

  return {
    items: processed,
    resultCount: processed.length,
    totalCount: Array.isArray(items) ? items.length : 0,
    isFiltered,
    filterSignature,
    search: { value: searchInput, onChange: onSearchChange },
    filterList,
    filters: filterMap,
    sort,
    clear,
  };
};

export default useListControls;
